import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';

const CyberAegisLogin = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }
        setIsLoading(true);
        setError('');

        const result = await onLogin(email, password);

        if (result && !result.success) {
            setError(result.message || 'Login failed. Please try again.');
        }
        setIsLoading(false);
    };

    return (

        <div className="min-h-screen flex items-center justify-center relative px-3 sm:px-4 py-24 sm:py-10">
            <div className="fixed inset-0" style={{ backgroundImage: "url('/assets/images/landing_page2.png')", backgroundSize: 'cover', backgroundPosition: 'center', zIndex: -2 }}></div>
            <div className="fixed inset-0 bg-black/60" style={{ zIndex: -1 }}></div>

            {/* Our Logo Section */}
            <div className="absolute top-2 left-3 sm:left-6 flex flex-col items-start sm:items-center z-10">
                <img
                    src="/assets/images/jumps_contrast.png"
                    alt="Jumps Logo"
                    className="h-14 w-14 sm:h-24 sm:w-24 object-contain"
                />
                <p className="text-gray-300 text-[10px] sm:text-base font-medium tracking-wide">© Jumps Automation LLP</p>
            </div>

            {/* Partner Logo Section */}
            {/* <div className="absolute top-3 right-3 sm:top-6 sm:right-6 flex flex-col items-end z-10">
                <p className="hidden sm:block text-gray-300 text-sm mb-2 font-medium tracking-wide">Our Partners in the Middle-East</p>
                <img
                    src="/assets/images/LOF-Logo.png"
                    alt="LOF Logo"
                    className="h-8 sm:h-14 w-auto object-contain"
                />
            </div> */}

            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center h-28 w-28 sm:h-42 sm:w-42 rounded-xl mb-4">
                        <img
                            src={'/assets/images/logo.png'}
                            alt="User Avatar"
                            className="w-28 h-28 sm:w-41 sm:h-41 cursor-pointer transition-all"
                        />
                    </div>
                    <p className="text-white mt-2 text-base sm:text-xl">Cyber Security Learning and Playing Platform</p>
                </div>

                <div className="bg-neon-600/80 backdrop-blur-sm shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] text-white font-bold px-4 sm:px-6 py-4 sm:py-5 rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 disabled:bg-gray-600 disabled:cursor-not-allowed">
                    {/* <h2 className="text-xl font-bold text-white mb-6 text-center">Sign In</h2> */}

                    {error && (
                        <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-md flex items-start">
                            <AlertCircle size={18} className="text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-red-200 text-sm">{error}</span>
                            <span className="text-red-200 text-sm">
                                {error.includes('pending activation') || error.includes('inactive') ? (
                                    <>
                                        <strong>Account Not Active:</strong> {error} <br />
                                        Please contact your administrator for account activation.
                                    </>
                                ) : error}
                            </span>
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        {/* --- EMAIL INPUT FIELD --- */}
                        <div className="mb-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail size={18} className="text-gray-500" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white w-full pl-10 pr-3 py-2 rounded-md focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
                                    placeholder="you@example.com"
                                />
                            </div>
                        </div>

                        {/* --- PASSWORD INPUT FIELD --- */}
                        <div className="mb-6">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-500" />
                                </div>
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white w-full pl-10 pr-10 py-2 rounded-md focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
                                    placeholder="••••••••"
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="text-gray-400 hover:text-gray-300 focus:outline-none"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-neon-600/80 backdrop-blur-sm shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-600/20 hover:shadow-[0_0_10px_#0ff,0_0_10px_#0ff,0_0_10px_#0ff] disabled:bg-gray-600 disabled:cursor-not-allowed w-full flex items-center justify-center space-x-2"
                        >
                            {isLoading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : "Sign In"}
                        </button>
                    </form>

                    <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300">
                            Forgot your password?
                        </Link>
                    </div>
                </div>

                <div className="mt-6 text-center text-gray-400 text-sm">
                    <p>
                        Don't have an account?{' '}
                        <Link to="/register" className="font-medium text-blue-400 hover:text-blue-300">
                            Sign Up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CyberAegisLogin;